/**
 * Native "Sign in with Google" — Android/iOS only, via
 * @capgo/capacitor-social-login (Credential Manager on Android, native
 * GoogleSignIn on iOS). The browser redirect flow (GoogleSignInButton's
 * `start()`) doesn't work inside Capacitor's WebView — Google blocks OAuth
 * there outright ("disallowed_useragent") — so this is a separate path that
 * never navigates the WebView at all.
 *
 * `webClientId` is intentionally the WEB OAuth client, not an Android/iOS
 * one — that's what Credential Manager requires, and it's also the exact
 * audience apps/api's verifyGoogleIdToken() already checks, so the backend
 * needed zero changes to accept these tokens (see POST /auth/google/native).
 */
import { SocialLogin } from '@capgo/capacitor-social-login';

const WEB_CLIENT_ID = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined;

let initialized = false;

async function ensureInitialized(): Promise<void> {
  if (initialized) return;
  if (!WEB_CLIENT_ID) {
    throw new Error('VITE_GOOGLE_WEB_CLIENT_ID is not set — native Google sign-in is unavailable.');
  }
  await SocialLogin.initialize({
    google: {
      webClientId: WEB_CLIENT_ID,
      // Same value on purpose (see file header) — only matters if we ever
      // switch to mode: 'offline', harmless to set now.
      iOSServerClientId: WEB_CLIENT_ID,
    },
  });
  initialized = true;
}

/** Whether the native button should render at all (config present). */
export function isNativeGoogleAuthAvailable(): boolean {
  return !!WEB_CLIENT_ID;
}

/**
 * Runs the native account picker and returns a verified-by-Google ID token
 * ready to POST to /auth/google/native. Throws with `code === 'USER_CANCELLED'`
 * (via the plugin's error shape) when the user dismisses the picker.
 */
export async function signInWithGoogleNative(): Promise<string> {
  await ensureInitialized();
  const { result } = await SocialLogin.login({
    provider: 'google',
    options: { scopes: ['email', 'profile'] },
  });
  // `mode` defaults to 'online', so this is always the Online shape — the
  // Offline variant (serverAuthCode only) is never reached with our config.
  const idToken = 'idToken' in result ? result.idToken : null;
  if (!idToken) {
    throw new Error('Google did not return an ID token.');
  }
  return idToken;
}
